import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { posPath } from '../posPath';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Smartphone, Lock, AlertCircle, Loader } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const POSSetupFlow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setupToken = searchParams.get('token');

  const [step, setStep] = useState(1); // 1: phone, 2: OTP, 3: PIN, 4: success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  useEffect(() => {
    if (!setupToken) {
      setError('Invalid or missing setup link');
    }
  }, [setupToken]);

  // Step 1: Enter phone and request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API}/api/rest/v1/pos/auth/request-setup-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setup_token: setupToken, phone })
      });

      const data = await res.json();

      if (data.success) {
        setStep(2);
      } else {
        setError(data.detail || data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API}/api/rest/v1/pos/auth/verify-setup-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setup_token: setupToken, phone, otp_code: otp })
      });

      const data = await res.json();

      if (data.success) {
        setStep(3);
        setOtp(''); // Clear OTP for security
      } else {
        setError(data.detail || data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Create PIN
  const handleCreatePIN = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (pin !== confirmPin) {
      setError('PINs do not match');
      setLoading(false);
      return;
    }

    if (pin.length !== 6 || !/^\d+$/.test(pin)) {
      setError('PIN must be exactly 6 digits');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API}/api/rest/v1/pos/auth/create-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          setup_token: setupToken, 
          phone, 
          pin, 
          confirm_pin: confirmPin 
        })
      });

      const data = await res.json();

      if (data.success) {
        // Store auth token and device token
        if (data.token) {
          localStorage.setItem('pos_token', data.token);
        }
        if (data.device_token) {
          localStorage.setItem('pos_device_token', data.device_token);
        }
        
        setStep(4);
        
        // Redirect to POS after 3 seconds
        setTimeout(() => {
          navigate(posPath(''));
        }, 3000);
      } else {
        setError(data.detail || data.message || 'Failed to create PIN');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!setupToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Invalid Setup Link</h2>
            <p className="text-gray-600">This setup link is invalid or has expired. Please contact your manager.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center border-b">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">POS Setup</CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            {step === 1 && "Let's verify your phone number"}
            {step === 2 && "Enter the code we sent you"}
            {step === 3 && "Create your secure 6-digit PIN"}
            {step === 4 && "Setup complete!"}
          </p>
        </CardHeader>

        <CardContent className="p-6">
          {/* Progress Steps */}
          <div className="flex justify-between mb-8">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold
                  ${step >= num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {step > num ? <CheckCircle className="w-6 h-6" /> : num}
                </div>
                <span className="text-xs mt-1 text-gray-600">
                  {num === 1 && 'Phone'}
                  {num === 2 && 'OTP'}
                  {num === 3 && 'PIN'}
                  {num === 4 && 'Done'}
                </span>
              </div>
            ))}
          </div>

          {/* Step 1: Phone Number */}
          {step === 1 && (
            <form onSubmit={handleRequestOTP} className="space-y-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Smartphone className="w-4 h-4" />
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  placeholder="+1 (234) 567-8900"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="text-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  We'll send a verification code to this number
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </Button>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div>
                <Label className="mb-2 block">Verification Code</Label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Code sent to {phone}
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button type="submit" disabled={loading || otp.length !== 6} className="flex-1">
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </Button>
              </div>

              <button
                type="button"
                onClick={handleRequestOTP}
                className="w-full text-sm text-blue-600 hover:underline"
              >
                Resend Code
              </button>
            </form>
          )}

          {/* Step 3: Create PIN */}
          {step === 3 && (
            <form onSubmit={handleCreatePIN} className="space-y-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4" />
                  Create 6-Digit PIN
                </Label>
                <Input
                  type="password"
                  placeholder="••••••"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
              </div>

              <div>
                <Label className="mb-2 block">Confirm PIN</Label>
                <Input
                  type="password"
                  placeholder="••••••"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
              </div>

              <div className="p-3 bg-blue-50 rounded text-sm text-blue-900">
                <p className="font-medium mb-1">PIN Requirements:</p>
                <ul className="text-xs space-y-1 list-disc list-inside">
                  <li>Exactly 6 digits</li>
                  <li>Avoid simple patterns (123456, 000000)</li>
                  <li>Keep it secret and secure</li>
                </ul>
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button 
                type="submit" 
                disabled={loading || pin.length !== 6 || confirmPin.length !== 6}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating PIN...
                  </>
                ) : (
                  'Complete Setup'
                )}
              </Button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Setup Complete!</h3>
              <p className="text-gray-600 mb-4">
                Your POS access is now active. You can login with your 6-digit PIN.
              </p>
              <div className="inline-block animate-spin">
                <Loader className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Redirecting to POS...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default POSSetupFlow;
